import React from 'react';
import SchoolComparisonCard from './SchoolComparisonCard';

const TierComparisonView = ({ tier, colleges, studentProfile, analysis, schoolInsights, loadingInsights, onDeleteCollege }) => {
  console.log(`🎨 [TierComparisonView] Rendering ${tier} tier:`, {
    collegeCount: colleges.length,
    hasSchoolInsights: !!schoolInsights,
    schoolInsightsKeys: schoolInsights ? Object.keys(schoolInsights) : [],
    loadingInsights,
    collegeNames: colleges.map(c => c.global_colleges?.name || c.name)
  });

  // Tier colors and labels
  const tierConfig = {
    reach: {
      label: 'Reach Tier Comparison',
      description: `Compare your edge and fit across all ${colleges.length} highly selective schools`,
      confidenceLabel: 'Tier Confidence',
      headerBg: 'bg-white',
      headerBorder: 'border-coral-400',
      confidenceColor: 'text-coral-400'
    },
    target: {
      label: 'Target Tier Comparison',
      description: `Compare your competitive match schools${colleges.length < 4 ? ' - you need ' + (4 - colleges.length) + ' more' : ''}`,
      confidenceLabel: 'Tier Status',
      headerBg: 'bg-white',
      headerBorder: 'border-teal-300',
      confidenceColor: 'text-slate-900'
    },
    safety: {
      label: 'Safety Tier Comparison',
      description: 'Compare schools where admission is likely and you\'d be genuinely happy',
      confidenceLabel: 'Tier Confidence',
      headerBg: 'bg-white',
      headerBorder: 'border-gold-300',
      confidenceColor: 'text-slate-900'
    }
  };

  const config = tierConfig[tier] || tierConfig.target;

  // Get confidence score from analysis
  const getConfidenceDisplay = () => {
    if (analysis?.confidence) {
      return `${analysis.confidence}/10`;
    }

    // Default confidence based on tier completion
    if (colleges.length >= 4) {
      return 'Strong';
    } else if (colleges.length >= 2) {
      return `${colleges.length}/4`;
    }
    return 'Developing';
  };

  return (
    <div>
      {/* Tier Header */}
      <div className={`${config.headerBg} border-2 ${config.headerBorder} rounded-lg p-6 mb-6`}>
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-2xl font-bold text-slate-900 mb-2">{config.label}</h3>
            <p className="text-sm text-slate-600">{config.description}</p>
          </div>
          <div className="text-right">
            <div className="text-sm font-semibold text-slate-600 mb-1">{config.confidenceLabel}</div>
            <div className={`text-3xl font-bold ${config.confidenceColor}`}>
              {getConfidenceDisplay()}
            </div>
          </div>
        </div>
      </div>

      {/* School Comparison Cards - Horizontal Scroll */}
      {colleges.length > 0 ? (
        <div className="horizontal-scroll flex gap-6 pb-4 mb-8 overflow-x-auto">
          {colleges.map((college) => {
            const collegeName = college.global_colleges?.name || college.name;
            const aiInsights = schoolInsights?.[collegeName] || null;

            console.log(`🏫 [${tier}] Card for "${collegeName}":`, {
              hasAiInsights: !!aiInsights,
              aiInsightsKeys: aiInsights ? Object.keys(aiInsights) : [],
              aiInsightsSample: aiInsights,
              loadingInsights
            });

            return (
              <SchoolComparisonCard
                key={college.id}
                college={college}
                tier={tier}
                studentProfile={studentProfile}
                fitScores={college.fitScores}
                insights={college.insights}
                aiInsights={aiInsights}
                loadingInsights={loadingInsights}
                onDelete={onDeleteCollege}
              />
            );
          })}
        </div>
      ) : (
        <div className="bg-gray-50 border-2 border-gray-200 rounded-lg p-8 text-center mb-8">
          <p className="text-sm text-gray-600">
            No {tier} schools added yet. Add schools to see detailed comparison cards.
          </p>
        </div>
      )}

      {/* Action Required Banner for Incomplete Tiers */}
      {tier === 'target' && colleges.length < 4 && (
        <div className="bg-gold-50 border-l-4 border-gold-500 rounded-r-lg p-5 mb-6">
          <div className="flex items-start gap-3">
            <div className="text-2xl">⚠️</div>
            <div>
              <div className="font-semibold text-slate-900 mb-1">Action Required</div>
              <div className="text-sm text-slate-700 mb-3">
                Add {4 - colleges.length} more Target school{4 - colleges.length > 1 ? 's' : ''} for proper balance. We recommend a school where your profile matches their median accepted student.
              </div>
              <button className="text-sm font-semibold text-teal-600 hover:text-teal-700">
                View Recommended Target Schools →
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TierComparisonView;
